import { useCallback, useState } from "react";

import { validatePassword } from "../components/Authentication/components/utils";
import { validateEmail } from "../utils";

import type { EmailAndPasswordFormErrorState } from "../components/Authentication/types";

const validationFns: Record<string, (val: string) => string | null> = {
  email: validateEmail,
  password: validatePassword,
};

interface UseSignupFormProps<T> {
  initialFormState: T;
}

interface UseSignupFormReturn<T> {
  form: T;
  formError: EmailAndPasswordFormErrorState;
  termsAccepted: boolean;
  handleFormChange: (key: keyof T, value: string) => void;
  handleTermsChange: (newValue: boolean) => void;
  validateEmailMatch: (email: string, confirmEmail: string) => string | null;
  setFormError: React.Dispatch<
    React.SetStateAction<EmailAndPasswordFormErrorState>
  >;
}

export function useSignupForm<T extends Record<string, any>>({
  initialFormState,
}: UseSignupFormProps<T>): UseSignupFormReturn<T> {
  const [form, setForm] = useState<T>(initialFormState);
  const [formError, setFormError] = useState<EmailAndPasswordFormErrorState>({
    email: null,
    password: null,
  });
  const [termsAccepted, setTermsAccepted] = useState<boolean>(false);

  const handleFormChange = useCallback((key: keyof T, value: string) => {
    setForm((prev) => ({
      ...prev,
      [key]: value,
    }));
    if (["email", "password"].includes(key as string)) {
      setFormError((prev) => ({
        ...prev,
        [key]: validationFns[key as string](value),
      }));
    }
  }, []);

  const handleTermsChange = useCallback((newValue: boolean) => {
    setTermsAccepted(newValue);
  }, []);

  const validateEmailMatch = useCallback(
    (email: string, confirmEmail: string): string | null =>
      email !== confirmEmail ? "Emails do not match." : null,
    [],
  );

  return {
    form,
    formError,
    termsAccepted,
    handleFormChange,
    handleTermsChange,
    validateEmailMatch,
    setFormError,
  };
}
